import { hex, shade, W, type Col } from "../art/pal.ts";
import { MAP, MAP_H, MAP_W, SPOTS, TILE, zoneAt, type TileId, type ZoneId } from "../data/map.ts";
import {
  BIOME, biomeOf, cactusSprite, cloudBushSprite, coralSprite, deadTreeSprite, GROUND, hayBaleSprite, holoTreeSprite, isTown, jungleTreeSprite,
  palmSprite, PATH, pillarSprite, pylonSprite, ROCK, ruinSprite, SHORE, shroomSprite, snowCapped, spireSprite, stalagmiteSprite, stoneLanternSprite,
  tintFoliage, tintStone, tombSprite, type Biome,
} from "./biome.ts";
import { bayer, fbm, hash2, vnoise, worley } from "./noise.ts";
import { boulderSprite, barrelSprite, crateSprite, fenceSprite, kettleSprite, lampSprite, pineSprite, reedSprite, signSprite, tallTuft, treeSprite, type PropSprite } from "./props.ts";
import { buildingSprite, type BuildingKind } from "./buildings.ts";

export const WPX = MAP_W * TILE;
export const HPX = MAP_H * TILE;

/** Ground classes, in paint order. */
const enum G {
  Grass = 0,
  Forest = 1,
  Lush = 2,
  Plaza = 3,
  Road = 4,
  Dirt = 5,
  Rock = 6,
  Water = 7,
  Sand = 8,
}

export type ObjKind = "tree" | "pine" | "boulder" | "lamp" | "crate" | "barrel" | "fence" | "sign" | "reed" | "kettle" | "tall" | "building"
  | "cactus" | "deadtree" | "spire"
  | "stalagmite" | "shroom" | "palm" | "coral" | "cloudbush" | "pillar" | "holotree" | "pylon"
  | "jungletree" | "hay" | "tomb" | "stonelantern" | "ruin";

export interface WorldObj {
  kind: ObjKind;
  /** Ground anchor in world pixels. */
  x: number;
  y: number;
  tx: number;
  ty: number;
  variant: number;
  building?: { kind: BuildingKind; tw: number; th: number; doors: number[] };
  fence?: [boolean, boolean];
  biome?: Biome;
}

export interface WorldLight {
  x: number;
  y: number;
  r: number;
  color: Col;
  /** Only shines at night (lamps, windows) vs always (kettle fire). */
  nightOnly: boolean;
  flicker?: boolean;
}

export interface World {
  ground: ImageData;
  cls: Uint8Array;
  water: Uint8Array;
  depth: Float32Array;
  /** Water: rows of land directly above (bank face 1..3), 0 otherwise. */
  bank: Uint8Array;
  /** Water: distance to nearest land (Chebyshev, capped). */
  shore: Uint8Array;
  objects: WorldObj[];
  lights: WorldLight[];
  /** Chimney tops for smoke. */
  chimneys: { x: number; y: number }[];
  /** Biome per tile (see biome.ts). */
  biome: Uint8Array;
}

let world: World | null = null;

export function getWorld(): World {
  if (!world) world = bake();
  return world;
}

function tile(tx: number, ty: number): TileId {
  if (tx < 0 || ty < 0 || tx >= MAP_W || ty >= MAP_H) return "wall";
  return MAP[ty]![tx]!;
}

function isBuildingTile(id: TileId): boolean {
  return id === "roof" || id === "door" || id === "counter" || id === "wood";
}

/** Connected components of `pred` tiles (4-way). */
function components(pred: (id: TileId) => boolean): { tiles: [number, number][]; x0: number; y0: number; x1: number; y1: number }[] {
  const seen = new Uint8Array(MAP_W * MAP_H);
  const out: { tiles: [number, number][]; x0: number; y0: number; x1: number; y1: number }[] = [];
  for (let y = 0; y < MAP_H; y++) {
    for (let x = 0; x < MAP_W; x++) {
      if (seen[y * MAP_W + x] || !pred(tile(x, y))) continue;
      const q: [number, number][] = [[x, y]];
      seen[y * MAP_W + x] = 1;
      const tiles: [number, number][] = [];
      let x0 = x, y0 = y, x1 = x, y1 = y;
      while (q.length) {
        const [cx, cy] = q.pop()!;
        tiles.push([cx, cy]);
        x0 = Math.min(x0, cx); y0 = Math.min(y0, cy); x1 = Math.max(x1, cx); y1 = Math.max(y1, cy);
        for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
          const nx = cx + dx, ny = cy + dy;
          if (nx < 0 || ny < 0 || nx >= MAP_W || ny >= MAP_H) continue;
          if (seen[ny * MAP_W + nx] || !pred(tile(nx, ny))) continue;
          seen[ny * MAP_W + nx] = 1;
          q.push([nx, ny]);
        }
      }
      out.push({ tiles, x0, y0, x1, y1 });
    }
  }
  return out;
}

function boxBlur(src: Float32Array, w: number, h: number, r: number): Float32Array {
  const tmp = new Float32Array(w * h);
  const out = new Float32Array(w * h);
  const win = r * 2 + 1;
  for (let y = 0; y < h; y++) {
    let acc = 0;
    for (let k = -r; k <= r; k++) acc += src[y * w + Math.max(0, Math.min(w - 1, k))]!;
    for (let x = 0; x < w; x++) {
      tmp[y * w + x] = acc / win;
      const add = Math.min(w - 1, x + r + 1), sub = Math.max(0, x - r);
      acc += src[y * w + add]! - src[y * w + sub]!;
    }
  }
  for (let x = 0; x < w; x++) {
    let acc = 0;
    for (let k = -r; k <= r; k++) acc += tmp[Math.max(0, Math.min(h - 1, k)) * w + x]!;
    for (let y = 0; y < h; y++) {
      out[y * w + x] = acc / win;
      const add = Math.min(h - 1, y + r + 1), sub = Math.max(0, y - r);
      acc += tmp[add * w + x]! - tmp[sub * w + x]!;
    }
  }
  return out;
}

function blur2(src: Float32Array, r: number): Float32Array {
  return boxBlur(boxBlur(src, WPX, HPX, r), WPX, HPX, r);
}

function zoneOf(tx: number, ty: number): ZoneId {
  return zoneAt(Math.max(0, Math.min(MAP_W - 1, tx)), Math.max(0, Math.min(MAP_H - 1, ty)));
}

function bake(): World {
  const tileBiome = new Uint8Array(MAP_W * MAP_H);
  for (let ty = 0; ty < MAP_H; ty++) for (let tx = 0; tx < MAP_W; tx++) tileBiome[ty * MAP_W + tx] = biomeOf(zoneOf(tx, ty));
  // Biome seams wander: sample the tile biome through a noise offset so snow, sand and ash
  // bleed into their neighbours instead of meeting on a ruler-straight tile line.
  // The seam noise is slow and smooth (38 px scale), so it is sampled every other pixel and the
  // result cached for the several passes below that ask for it.
  const pxBiome = new Uint8Array(WPX * HPX).fill(255);
  const biomeAt = (x: number, y: number): Biome => {
    const i = y * WPX + x;
    const hit = pxBiome[i]!;
    if (hit !== 255) return hit as Biome;
    const sx = x & ~1, sy = y & ~1;
    const jx = Math.round((fbm(sx / 38, sy / 38, 2, 71) - 0.5) * 30), jy = Math.round((fbm(sx / 38, sy / 38, 2, 72) - 0.5) * 30);
    const tx = Math.max(0, Math.min(MAP_W - 1, ((x + jx) / TILE) | 0)), ty = Math.max(0, Math.min(MAP_H - 1, ((y + jy) / TILE) | 0));
    const b = tileBiome[ty * MAP_W + tx]!;
    pxBiome[i] = b;
    return b as Biome;
  };
  // ---- 1. Per-tile ground class -------------------------------------------------------------
  const rockComps = components((id) => id === "rock");
  const outcrop = new Uint8Array(MAP_W * MAP_H);
  for (const c of rockComps) if (c.tiles.length >= 4) for (const [x, y] of c.tiles) outcrop[y * MAP_W + x] = 1;

  const direct = (tx: number, ty: number): number => {
    const id = tile(tx, ty);
    const town = isTown(zoneOf(tx, ty));
    switch (id) {
      case "wall": return G.Forest;
      case "tall": return G.Lush;
      case "floor": return G.Plaza;
      case "path":
      case "rest": return town ? G.Road : G.Dirt;
      case "water": case "foam": case "reed": return G.Water;
      case "rock": return outcrop[ty * MAP_W + tx] ? G.Rock : -1;
      case "roof": case "door": case "counter": case "wood": return G.Plaza;
      case "grass": case "flower": case "tree": return G.Grass;
      default: return -1; // object on inherited ground
    }
  };
  const tg = new Int8Array(MAP_W * MAP_H);
  for (let ty = 0; ty < MAP_H; ty++) for (let tx = 0; tx < MAP_W; tx++) tg[ty * MAP_W + tx] = direct(tx, ty);
  for (let pass = 0; pass < 4; pass++) {
    for (let ty = 0; ty < MAP_H; ty++) {
      for (let tx = 0; tx < MAP_W; tx++) {
        if (tg[ty * MAP_W + tx]! >= 0) continue;
        const counts = new Map<number, number>();
        for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
          const nx = tx + dx, ny = ty + dy;
          if (nx < 0 || ny < 0 || nx >= MAP_W || ny >= MAP_H) continue;
          const v = tg[ny * MAP_W + nx]!;
          if (v < 0 || v === G.Water || v === G.Forest) continue;
          counts.set(v, (counts.get(v) ?? 0) + 1);
        }
        let best = -1, n = 0;
        for (const [k, c] of counts) if (c > n) { best = k; n = c; }
        if (best >= 0) tg[ty * MAP_W + tx] = best;
        else if (pass === 3) tg[ty * MAP_W + tx] = isTown(zoneOf(tx, ty)) ? G.Plaza : G.Grass;
      }
    }
  }

  // ---- 2. Soft fields per overlay layer -----------------------------------------------------
  const layers = [G.Forest, G.Lush, G.Plaza, G.Road, G.Dirt, G.Rock, G.Water];
  const fields = new Map<number, Float32Array>();
  for (const L of layers) {
    const f = new Float32Array(WPX * HPX);
    for (let y = 0; y < HPX; y++) {
      const ty = (y / TILE) | 0;
      for (let x = 0; x < WPX; x++) f[y * WPX + x] = tg[ty * MAP_W + ((x / TILE) | 0)] === L ? 1 : 0;
    }
    fields.set(L, blur2(f, L === G.Water ? 4 : L === G.Rock ? 3 : 4));
  }
  const waterDeep = (() => {
    const f = new Float32Array(WPX * HPX);
    for (let y = 0; y < HPX; y++) for (let x = 0; x < WPX; x++) f[y * WPX + x] = tg[((y / TILE) | 0) * MAP_W + ((x / TILE) | 0)] === G.Water ? 1 : 0;
    return blur2(f, 9);
  })();

  // ---- 3. Classify -----------------------------------------------------------------------------
  const cls = new Uint8Array(WPX * HPX);
  const order = [G.Water, G.Rock, G.Road, G.Dirt, G.Plaza, G.Lush, G.Forest];
  for (let y = 0; y < HPX; y++) {
    for (let x = 0; x < WPX; x++) {
      const i = y * WPX + x;
      let c = G.Grass;
      const n = (fbm(x * 0.09, y * 0.09, 2, 7) - 0.5) * 0.42;
      for (const L of order) {
        const amp = L === G.Water ? 0.6 : L === G.Forest || L === G.Lush ? 1.3 : 1;
        if (fields.get(L)![i]! + n * amp > 0.5) { c = L; break; }
      }
      cls[i] = c;
    }
  }
  // Water mask / shore distances.
  const water = new Uint8Array(WPX * HPX);
  for (let i = 0; i < cls.length; i++) water[i] = cls[i] === G.Water ? 1 : 0;
  const shore = new Uint8Array(WPX * HPX);
  const bank = new Uint8Array(WPX * HPX);
  // Chebyshev distance from water to the nearest land pixel (capped at 9): a two-pass chamfer
  // transform with unit weights on all eight neighbours, exact for the Chebyshev metric.
  for (let i = 0; i < shore.length; i++) shore[i] = water[i] ? 9 : 0;
  for (let y = 0; y < HPX; y++) {
    for (let x = 0; x < WPX; x++) {
      const i = y * WPX + x;
      if (!shore[i]) continue;
      let d = shore[i]!;
      if (x > 0) d = Math.min(d, shore[i - 1]! + 1);
      if (y > 0) {
        d = Math.min(d, shore[i - WPX]! + 1);
        if (x > 0) d = Math.min(d, shore[i - WPX - 1]! + 1);
        if (x + 1 < WPX) d = Math.min(d, shore[i - WPX + 1]! + 1);
      }
      shore[i] = d;
    }
  }
  for (let y = HPX - 1; y >= 0; y--) {
    for (let x = WPX - 1; x >= 0; x--) {
      const i = y * WPX + x;
      if (!shore[i]) continue;
      let d = shore[i]!;
      if (x + 1 < WPX) d = Math.min(d, shore[i + 1]! + 1);
      if (y + 1 < HPX) {
        d = Math.min(d, shore[i + WPX]! + 1);
        if (x + 1 < WPX) d = Math.min(d, shore[i + WPX + 1]! + 1);
        if (x > 0) d = Math.min(d, shore[i + WPX - 1]! + 1);
      }
      shore[i] = d;
    }
  }
  for (let y = 0; y < HPX; y++) {
    for (let x = 0; x < WPX; x++) {
      const i = y * WPX + x;
      if (!water[i]) continue;
      for (let k = 1; k <= 4; k++) {
        if (y - k >= 0 && !water[(y - k) * WPX + x]) { bank[i] = k; break; }
      }
    }
  }
  // Sand shore ring on land: any water within |dx|,|dy| <= 2 and |dx| + |dy| <= 3. Row runs make
  // that a few lookups per pixel: water within ±2 on the rows y-1..y+1, or within ±1 on y±2.
  const run2 = new Uint8Array(WPX * HPX), run1 = new Uint8Array(WPX * HPX);
  for (let y = 0; y < HPX; y++) {
    const o = y * WPX;
    for (let x = 0; x < WPX; x++) {
      let w1 = 0, w2 = 0;
      for (let dx = -2; dx <= 2; dx++) {
        const px = x + dx;
        if (px < 0 || px >= WPX || !water[o + px]) continue;
        w2 = 1;
        if (dx >= -1 && dx <= 1) w1 = 1;
      }
      run2[o + x] = w2; run1[o + x] = w1;
    }
  }
  for (let y = 0; y < HPX; y++) {
    for (let x = 0; x < WPX; x++) {
      const i = y * WPX + x;
      const c = cls[i]!;
      if (c === G.Water || c === G.Rock || c === G.Forest) continue;
      const near = run2[i] || (y > 0 && run2[i - WPX]) || (y + 1 < HPX && run2[i + WPX])
        || (y > 1 && run1[i - 2 * WPX]) || (y + 2 < HPX && run1[i + 2 * WPX]);
      if (near && hash2(x, y, 31) > 0.12) cls[i] = G.Sand;
    }
  }

  // ---- 4. Texture ------------------------------------------------------------------------------
  const ground = new ImageData(WPX, HPX);
  const d = ground.data;
  const put = (i: number, c: Col) => {
    const o = i * 4;
    d[o] = c[0]; d[o + 1] = c[1]; d[o + 2] = c[2]; d[o + 3] = 255;
  };
  const grassLike = (c: number) => c === G.Grass || c === G.Lush || c === G.Forest;
  for (let y = 0; y < HPX; y++) {
    for (let x = 0; x < WPX; x++) {
      const i = y * WPX + x;
      put(i, texel(cls[i]!, x, y, waterDeep[i]!, biomeAt(x, y)));
    }
  }
  // Rock outcrops: cliff face on the lowest rows of the rock mass.
  for (let y = 0; y < HPX; y++) {
    for (let x = 0; x < WPX; x++) {
      const i = y * WPX + x;
      if (cls[i] !== G.Rock) continue;
      let dd = 0;
      while (dd < 10 && y + dd + 1 < HPX && cls[(y + dd + 1) * WPX + x] === G.Rock) dd++;
      const rb = biomeAt(x, y);
      if (dd < 8) {
        const rr = rb ? ROCK[rb] : W.cliff;
        const col = Math.floor((x + Math.floor(vnoise(x * 0.3, 0, 5) * 3)) / 3);
        let t = 1.4 + (hash2(col, 0, 3) - 0.5) * 1.2 + (7 - dd) * 0.28;
        if (dd === 7) t = 0.2;
        if ((y + col) % 5 === 0) t += 0.6;
        const k = Math.max(0, Math.min(rr.length - 1, Math.floor(t + bayer(x, y) * 0.8)));
        put(i, rr[k]!);
      } else if (y > 0 && cls[(y - 1) * WPX + x] !== G.Rock) {
        put(i, rb ? ROCK[rb][0]! : W.rock[0]!);
      }
    }
  }
  // Grass lip shadows on paths / plaza, and bright tuft edges.
  for (let y = HPX - 1; y >= 1; y--) {
    for (let x = 0; x < WPX; x++) {
      const i = y * WPX + x;
      const c = cls[i]!;
      if (grassLike(c) || c === G.Water || c === G.Rock) continue;
      const up1 = cls[(y - 1) * WPX + x]!;
      const up2 = y > 1 ? cls[(y - 2) * WPX + x]! : c;
      const o = i * 4;
      const k = grassLike(up1) || up1 === G.Rock ? 0.72 : grassLike(up2) || up2 === G.Rock ? 0.86 : 1;
      if (k < 1) { d[o] = d[o]! * k; d[o + 1] = d[o + 1]! * k; d[o + 2] = d[o + 2]! * (k + 0.05); }
    }
  }
  for (let y = 0; y < HPX - 1; y++) {
    for (let x = 0; x < WPX; x++) {
      const i = y * WPX + x;
      if (!grassLike(cls[i]!)) continue;
      const below = cls[i + WPX]!;
      const gb = biomeAt(x, y);
      const gr = gb ? GROUND[gb] : W.grass;
      if (!grassLike(below) && below !== G.Water) {
        // Overhanging blades: some edge pixels poke down one row.
        put(i, gr[1]!);
        if (hash2(x, y, 44) < 0.35 && y + 1 < HPX) put(i + WPX, gr[2]!);
      }
      const above = y > 0 ? cls[i - WPX]! : cls[i]!;
      if (!grassLike(above) && above !== G.Water && above !== G.Rock) put(i, gr[4]!);
    }
  }

  // ---- 5. Decals ----------------------------------------------------------------------------
  const decal = (x: number, y: number, c: Col) => {
    if (x < 0 || y < 0 || x >= WPX || y >= HPX) return;
    put(y * WPX + x, c);
  };
  for (let ty = 0; ty < MAP_H; ty++) {
    for (let tx = 0; tx < MAP_W; tx++) {
      const id = tile(tx, ty);
      const ox = tx * TILE, oy = ty * TILE;
      const tb = tileBiome[ty * MAP_W + tx] as Biome;
      if (tb !== BIOME.temperate && (id === "grass" || id === "flower")) {
        // Biome specks: snow glints, desert pebbles, embers in ash, bog puddles, crystal chips.
        for (let k = 0; k < (id === "flower" ? 5 : 2); k++) {
          if (hash2(tx, ty, 200 + k) > (id === "flower" ? 0.9 : 0.45)) continue;
          const fx = ox + 2 + Math.floor(hash2(tx, ty, 210 + k) * 12), fy = oy + 2 + Math.floor(hash2(tx, ty, 220 + k) * 12);
          if (tb === BIOME.snow) { decal(fx, fy, hex("#ffffff")); decal(fx + 1, fy + 1, hex("#bcd8ec")); }
          else if (tb === BIOME.desert) { decal(fx, fy, hex("#c89858")); decal(fx + 1, fy, hex("#fff1cc")); decal(fx, fy + 1, hex("#86603a")); }
          else if (tb === BIOME.volcano) { decal(fx, fy, hex("#ff8a3c")); decal(fx + 1, fy, hex("#7a1f2c")); }
          else if (tb === BIOME.swamp) { for (let dx = -1; dx <= 2; dx++) decal(fx + dx, fy, hex("#3a5046")); decal(fx, fy - 1, hex("#6a8a78")); }
          else if (tb === BIOME.cave) { decal(fx, fy, hex("#5ae8d0")); decal(fx + 1, fy + 1, hex("#1a2030")); }
          else if (tb === BIOME.reef) { decal(fx, fy, [hex("#ffffff"), hex("#ff9a8a"), hex("#ffd070")][k % 3]!); decal(fx + 1, fy, hex("#dcb074")); }
          else if (tb === BIOME.sky) { decal(fx, fy, hex("#fff0a0")); decal(fx - 1, fy, hex("#ffffff")); decal(fx + 1, fy, hex("#ffffff")); }
          else if (tb === BIOME.cyber) { decal(fx, fy, k % 2 ? hex("#3ef0e0") : hex("#ff5ad8")); }
          else if (tb === BIOME.autumn) { const lc = [hex("#ff8a3c"), hex("#ffd060"), hex("#c8402c")][k % 3]!; decal(fx, fy, lc); decal(fx + 1, fy, lc); decal(fx, fy + 1, hex("#8f3d2c")); }
          else if (tb === BIOME.haunted) { if (k % 2) { decal(fx, fy, hex("#e6e2f0")); decal(fx + 1, fy, hex("#bab4cc")); } else decal(fx, fy, hex("#9ae8c8")); }
          else if (tb === BIOME.jungle) { decal(fx, fy, k % 2 ? hex("#ff5a8a") : hex("#ffd84a")); decal(fx, fy + 1, hex("#1e522e")); decal(fx - 1, fy + 1, hex("#2c6e36")); }
          else if (tb === BIOME.blossom) { decal(fx, fy, hex("#ffb3cc")); decal(fx + 1, fy, hex("#ffe1ec")); if (k % 2) decal(fx, fy + 1, hex("#f28bb0")); }
          else if (tb === BIOME.ruins) { decal(fx, fy, hex("#a08c70")); decal(fx + 1, fy, hex("#e8dcc0")); decal(fx, fy + 1, hex("#5fae50")); }
          else { decal(fx, fy, hex("#ffffff")); decal(fx, fy + 1, hex("#a4f3ea")); decal(fx + 1, fy + 1, hex("#ae92f2")); }
        }
        continue;
      }
      if (id === "flower") {
        for (let k = 0; k < 6; k++) {
          const fx = ox + 2 + Math.floor(hash2(tx, ty, k) * 12), fy = oy + 3 + Math.floor(hash2(tx, ty, k + 9) * 11);
          const petal = W.flowers[Math.floor(hash2(tx + k, ty, 5) * 5)]!;
          decal(fx, fy + 1, W.grass[4]!);
          decal(fx - 1, fy + 2, W.grass[3]!);
          decal(fx, fy - 1, petal); decal(fx - 1, fy, petal); decal(fx + 1, fy, petal); decal(fx, fy + 1, shade(petal, 0.75));
          decal(fx, fy, hex("#fff0a8"));
        }
      } else if (id === "grass" && hash2(tx, ty, 77) < 0.22) {
        const fx = ox + 3 + Math.floor(hash2(tx, ty, 78) * 10), fy = oy + 3 + Math.floor(hash2(tx, ty, 79) * 10);
        const petal = W.flowers[Math.floor(hash2(tx, ty, 80) * 6)]!;
        decal(fx, fy, petal);
        decal(fx + 2, fy + 1, petal);
        decal(fx + 1, fy + 3, W.grass[1]!);
      } else if ((id === "path" || id === "rest") && !isTown(zoneOf(tx, ty)) && tb === BIOME.temperate && hash2(tx, ty, 90) < 0.3) {
        const fx = ox + 2 + Math.floor(hash2(tx, ty, 91) * 12), fy = oy + 2 + Math.floor(hash2(tx, ty, 92) * 12);
        decal(fx, fy, W.dirt[0]!); decal(fx + 1, fy, W.dirt[1]!); decal(fx, fy + 1, W.dirt[4]!); decal(fx + 1, fy + 1, W.dirt[4]!);
      }
    }
  }
  // Plaza medallion in Stubtown's square.
  paintMedallion(ground, 7 * TILE + 8, 9 * TILE + 8, cls);

  // ---- 6. Objects ---------------------------------------------------------------------------
  const objects: WorldObj[] = [];
  const lights: WorldLight[] = [];
  const chimneys: { x: number; y: number }[] = [];
  const add = (kind: ObjKind, tx: number, ty: number, dx = 0, dy = 0, variant = 0, extra: Partial<WorldObj> = {}) =>
    objects.push({ kind, tx, ty, x: tx * TILE + 8 + dx, y: ty * TILE + 15 + dy, variant, ...extra });

  const buildings = components((id) => isBuildingTile(id));
  for (const b of buildings) {
    const tw = b.x1 - b.x0 + 1, th = b.y1 - b.y0 + 1;
    const doors: number[] = [];
    for (let x = b.x0; x <= b.x1; x++) if (tile(x, b.y1) === "door") doors.push(x - b.x0);
    // The service behind a door names the building; the old corner heuristics cover the rest.
    const doorSpot = doors.map((dc) => SPOTS.find((sp) => sp.x === b.x0 + dc && sp.y === b.y1)).find(Boolean);
    const kind: BuildingKind = doorSpot?.kind === "heal" ? "clinic" : doorSpot?.kind === "shop" ? "stall" : doorSpot?.kind === "pc" ? "box"
      : doorSpot?.kind === "market" ? "market" : doorSpot?.kind === "raid" ? "gate"
      : doorSpot?.kind === "crowns" ? "crownhouse" : doorSpot?.kind === "hall" ? "hall"
      : b.x0 <= 2 && b.y0 <= 2 ? "clinic" : b.x0 >= 10 && b.y0 <= 2 ? "stall" : tw <= 3 ? "box" : "house";
    objects.push({ kind: "building", tx: b.x0, ty: b.y1, x: b.x0 * TILE + (tw * TILE) / 2, y: (b.y1 + 1) * TILE - 1, variant: 0, building: { kind, tw, th, doors } });
    for (const dc of doors) lights.push({ x: (b.x0 + dc) * TILE + 8, y: (b.y1 + 1) * TILE - 8, r: 34, color: hex("#ffb45a"), nightOnly: true });
    for (let x = b.x0 * TILE + 12; x < (b.x1 + 1) * TILE - 8; x += 18) lights.push({ x, y: (b.y1 + 1) * TILE - 18, r: 16, color: hex("#ffc36a"), nightOnly: true });
    if (kind === "clinic" || kind === "house") chimneys.push({ x: b.x0 * TILE + tw * TILE - Math.min(10, Math.floor(tw * TILE * 0.12)) - 14 + 3 + 3, y: b.y0 * TILE - 20 });
  }
  const smallRocks = components((id) => id === "rock").filter((c) => c.tiles.length < 4);
  for (const c of smallRocks) for (const [x, y] of c.tiles) add("boulder", x, y, Math.round((hash2(x, y, 1) - 0.5) * 3), 0, Math.floor(hash2(x, y, 2) * 4), { biome: tileBiome[y * MAP_W + x] as Biome });

  for (let ty = 0; ty < MAP_H; ty++) {
    for (let tx = 0; tx < MAP_W; tx++) {
      const id = tile(tx, ty);
      const z = zoneOf(tx, ty);
      const h = hash2(tx, ty, 5);
      const tb = tileBiome[ty * MAP_W + tx] as Biome;
      const jx = Math.round((hash2(tx, ty, 6) - 0.5) * 5), jy = Math.round((hash2(tx, ty, 7) - 0.5) * 3);
      const v = Math.floor(hash2(tx, ty, 8) * 6);
      if ((id === "wall" || id === "tree") && tb !== BIOME.temperate) {
        if (tb === BIOME.snow) add("pine", tx, ty, jx, jy, v, { biome: tb });
        else if (tb === BIOME.desert) add(h < 0.7 ? "cactus" : "boulder", tx, ty, jx, jy, v, { biome: tb });
        else if (tb === BIOME.volcano) add(h < 0.55 ? "deadtree" : "boulder", tx, ty, jx, jy, v, { biome: tb });
        else if (tb === BIOME.swamp) add(h < 0.8 ? "deadtree" : "tree", tx, ty, jx, jy, v, { biome: tb });
        else if (tb === BIOME.cave) {
          const shroom = h < 0.3;
          add(shroom ? "shroom" : "stalagmite", tx, ty, jx, jy, v, { biome: tb });
          if (shroom) lights.push({ x: tx * TILE + 8 + jx, y: ty * TILE - 4, r: 28, color: v % 2 ? hex("#5ae8d0") : hex("#b4ff5a"), nightOnly: true });
        } else if (tb === BIOME.reef) add(h < 0.65 ? "palm" : "coral", tx, ty, jx, jy, v, { biome: tb });
        else if (tb === BIOME.sky) add(h < 0.6 ? "cloudbush" : "pillar", tx, ty, jx, jy, v, { biome: tb });
        else if (tb === BIOME.cyber) {
          const pylon = h >= 0.55;
          add(pylon ? "pylon" : "holotree", tx, ty, jx, jy, v, { biome: tb });
          lights.push({ x: tx * TILE + 8 + jx, y: ty * TILE - (pylon ? 26 : 12), r: pylon ? 22 : 30, color: v % 2 ? hex("#3ef0e0") : hex("#ff5ad8"), nightOnly: true });
        } else if (tb === BIOME.autumn) add(h < 0.88 ? "tree" : "hay", tx, ty, jx, jy, v, { biome: tb });
        else if (tb === BIOME.haunted) add(h < 0.7 ? "deadtree" : "tomb", tx, ty, jx, jy, v, { biome: tb });
        else if (tb === BIOME.jungle) add(h < 0.7 ? "jungletree" : "palm", tx, ty, jx, jy, v, { biome: tb });
        else if (tb === BIOME.blossom) {
          const lantern = h >= 0.9 && id === "tree";
          add(lantern ? "stonelantern" : "tree", tx, ty, lantern ? 0 : jx, lantern ? 0 : jy, v, { biome: tb });
          if (lantern) lights.push({ x: tx * TILE + 8, y: ty * TILE + 2, r: 34, color: hex("#ffcf7a"), nightOnly: true, flicker: true });
        } else if (tb === BIOME.ruins) add(h < 0.55 ? "ruin" : "tree", tx, ty, jx, jy, v, { biome: tb });
        else {
          add("spire", tx, ty, jx, jy, v, { biome: tb });
          if (h < 0.5) lights.push({ x: tx * TILE + 8 + jx, y: ty * TILE - 10, r: 26, color: v % 2 ? hex("#b09af0") : hex("#5fd6cf"), nightOnly: true });
        }
        continue;
      }
      if (id === "wall") {
        const pine = z === "copeCliffs" || z === "yieldYard" ? h < 0.7 : h < 0.35;
        add(pine ? "pine" : "tree", tx, ty, Math.round((hash2(tx, ty, 6) - 0.5) * 6), Math.round((hash2(tx, ty, 7) - 0.5) * 3), Math.floor(hash2(tx, ty, 8) * 6));
      } else if (id === "tree") {
        const kind = z === "yieldYard" || z === "copeCliffs" ? "pine" : "tree";
        add(kind, tx, ty, Math.round((hash2(tx, ty, 6) - 0.5) * 4), 0, Math.floor(hash2(tx, ty, 8) * 6) + (z === "chartwood" && h < 0.3 ? 100 : 0));
      } else if (id === "lamp") {
        add("lamp", tx, ty);
        lights.push({ x: tx * TILE + 8, y: ty * TILE - 12, r: 58, color: hex("#ffcf7a"), nightOnly: true, flicker: true });
      } else if (id === "crate") add("crate", tx, ty, 0, 0, Math.floor(h * 2));
      else if (id === "barrel") add(tb === BIOME.autumn ? "hay" : "barrel", tx, ty, 0, 0, Math.floor(h * 4), { biome: tb });
      else if (id === "fence") add("fence", tx, ty, 0, 0, 0, { fence: [tile(tx - 1, ty) === "fence", tile(tx + 1, ty) === "fence"] });
      else if (id === "post") add("sign", tx, ty);
      else if (id === "reed") add("reed", tx, ty, 0, -2, Math.floor(h * 4), { biome: tb });
      else if (id === "rest") {
        add("kettle", tx, ty, 0, -1);
        lights.push({ x: tx * TILE + 8, y: ty * TILE + 10, r: 30, color: hex("#ff9a4a"), nightOnly: false, flicker: true });
      } else if (id === "tall") add("tall", tx, ty, Math.round((hash2(tx, ty, 13) - 0.5) * 4), Math.round((hash2(tx, ty, 14) - 0.5) * 3), Math.floor(h * 4), { biome: tb });
      else if (id === "water" && tb === BIOME.haunted && hash2(tx, ty, 17) < 0.5) {
        lights.push({ x: tx * TILE + 8, y: ty * TILE + 8, r: 26, color: hex("#9ae8c8"), nightOnly: true });
      } else if (id === "water" && tb === BIOME.volcano && hash2(tx, ty, 15) < 0.45) {
        lights.push({ x: tx * TILE + 8, y: ty * TILE + 8, r: 30, color: hex("#ff7a3c"), nightOnly: false, flicker: true });
      } else if (id === "water" && tb === BIOME.crystal) {
        lights.push({ x: tx * TILE + 8, y: ty * TILE + 8, r: 24, color: hex("#5fd6cf"), nightOnly: true });
      } else if (id === "water" && (tb === BIOME.cave || tb === BIOME.cyber) && hash2(tx, ty, 16) < 0.5) {
        lights.push({ x: tx * TILE + 8, y: ty * TILE + 8, r: 26, color: tb === BIOME.cave ? hex("#3ec8b4") : hex("#3ef0e0"), nightOnly: true });
      }
    }
  }
  // Reeds also fringe some open-water edges.
  for (let ty = 0; ty < MAP_H; ty++) {
    for (let tx = 0; tx < MAP_W; tx++) {
      if (tile(tx, ty) !== "water" || hash2(tx, ty, 50) > 0.28) continue;
      const wb = tileBiome[ty * MAP_W + tx] as Biome;
      if (wb === BIOME.volcano || wb === BIOME.snow || wb === BIOME.crystal || wb === BIOME.cave || wb === BIOME.sky || wb === BIOME.cyber || wb === BIOME.haunted) continue;
      const land = [[0, -1], [-1, 0], [1, 0]].some(([dx, dy]) => { const t = tile(tx + dx!, ty + dy!); return t !== "water" && t !== "foam" && t !== "reed"; });
      if (land) add("reed", tx, ty, Math.round((hash2(tx, ty, 51) - 0.5) * 8), -6, Math.floor(hash2(tx, ty, 52) * 4), { biome: wb });
    }
  }

  // ---- 7. Static soft shadows from props (light from the upper-left → shadows fall right/down) --
  for (const o of objects) {
    if (o.kind === "tall" || o.kind === "reed" || o.kind === "kettle") continue;
    const big = o.kind === "tree" || o.kind === "pine" || o.kind === "cactus" || o.kind === "deadtree" || o.kind === "spire"
      || o.kind === "stalagmite" || o.kind === "palm" || o.kind === "holotree" || o.kind === "pillar" || o.kind === "pylon"
      || o.kind === "jungletree" || o.kind === "ruin";
    if (o.kind === "building") {
      const b = o.building!;
      const x0 = o.x - (b.tw * TILE) / 2, x1 = o.x + (b.tw * TILE) / 2;
      softShadow(ground, (x0 + x1) / 2 + 6, o.y + 2, (x1 - x0) / 2 + 4, 5, 0.34);
      continue;
    }
    const rx = big ? 15 : o.kind === "lamp" ? 6 : 8;
    const ry = big ? 6 : 3;
    softShadow(ground, o.x + (big ? 5 : 2), o.y + (big ? 1 : 1), rx, ry, big ? 0.4 : 0.3);
  }

  return { ground, cls, water, depth: waterDeep, bank, shore, objects, lights, chimneys, biome: tileBiome };
}

function softShadow(img: ImageData, cx: number, cy: number, rx: number, ry: number, a: number): void {
  const d = img.data;
  const tint = W.shadow;
  for (let y = Math.floor(cy - ry); y <= Math.ceil(cy + ry); y++) {
    if (y < 0 || y >= img.height) continue;
    for (let x = Math.floor(cx - rx); x <= Math.ceil(cx + rx); x++) {
      if (x < 0 || x >= img.width) continue;
      const nx = (x - cx) / rx, ny = (y - cy) / ry;
      const d2 = nx * nx + ny * ny;
      if (d2 >= 1) continue;
      const k = a * Math.min(1, (1 - d2) * 1.8);
      const o = (y * img.width + x) * 4;
      d[o] = d[o]! + (tint[0] - d[o]!) * k;
      d[o + 1] = d[o + 1]! + (tint[1] - d[o + 1]!) * k;
      d[o + 2] = d[o + 2]! + (tint[2] - d[o + 2]!) * k;
    }
  }
}

function paintMedallion(img: ImageData, cx: number, cy: number, cls: Uint8Array): void {
  const d = img.data;
  for (let y = cy - 14; y <= cy + 14; y++) {
    for (let x = cx - 20; x <= cx + 20; x++) {
      if (x < 0 || y < 0 || x >= img.width || y >= img.height) continue;
      if (cls[y * img.width + x] !== G.Plaza) continue;
      const nx = (x - cx) / 20, ny = (y - cy) / 14;
      const r = Math.sqrt(nx * nx + ny * ny);
      let c: Col | null = null;
      if (r < 1 && r > 0.9) c = W.plaza[3]!;
      else if (r <= 0.9 && r > 0.8) c = hex("#e8c77a");
      else if (r <= 0.8 && r > 0.74) c = W.plaza[3]!;
      else if (r < 0.3) c = (Math.floor(Math.atan2(ny, nx) * 4 / Math.PI) + 8) % 2 ? hex("#e8c77a") : W.plaza[0]!;
      else if (r < 0.74) {
        const a = Math.atan2(ny, nx);
        c = Math.floor((a + Math.PI) / (Math.PI / 8)) % 2 ? W.plaza[1]! : W.plaza[0]!;
        if (Math.abs(r - 0.5) < 0.03) c = W.plaza[3]!;
      }
      if (!c) continue;
      const o = (y * img.width + x) * 4;
      d[o] = c[0]; d[o + 1] = c[1]; d[o + 2] = c[2];
    }
  }
}

const BRICK: readonly Col[] = [hex("#f4b98e"), hex("#e39a74"), hex("#c97a5c"), hex("#a55e4c"), hex("#7a4440")];
const BRICK_MORTAR = hex("#d8c09a");

function dither(ramp: readonly Col[], f: number, x: number, y: number): Col {
  const b = Math.floor(f);
  const th = bayer(x, y) * 0.45 + hash2(x, y, 97) * 0.55;
  const i = f - b > th ? b + 1 : b;
  return ramp[Math.max(0, Math.min(ramp.length - 1, i))]!;
}

/** Lawn replacement per biome: snow drifts, wind-rippled dunes, ash, bog moss, crystal floor. */
function biomeGround(c: number, x: number, y: number, b: Exclude<Biome, 0>): Col {
  const ramp = GROUND[b];
  const big = fbm(x / 46, y / 46, 2, 1);
  const mid = vnoise(x / 7, y / 7, 2);
  let f = 2 + (big - 0.5) * 2.2 + (mid - 0.5) * 0.8;
  if (b === BIOME.desert) f += Math.sin((x + y * 0.35) / 5 + vnoise(x / 40, y / 40, 4) * 6) * 0.9;
  if (b === BIOME.snow) f -= 0.4 + (hash2(x, y, 3) > 0.985 ? 1.5 : 0);
  if (b === BIOME.volcano && vnoise(x / 9, y / 9, 81) > 0.83) return hex("#ff8a3c");
  if (b === BIOME.crystal && (Math.floor(x / 6) + Math.floor(y / 6)) % 2 === 0 && (x % 6 === 0 || y % 6 === 0)) f += 1.2;
  if (b === BIOME.cave) {
    // Wet stone: cracks and a faint sheen.
    const crack = Math.abs(vnoise(x / 11, y / 11, 83) - 0.5);
    if (crack < 0.02) f += 1.6;
    if (hash2(x, y, 84) > 0.992) return hex("#5ae8d0");
  }
  if (b === BIOME.reef) f += Math.sin((x * 0.6 + y) / 4 + vnoise(x / 30, y / 30, 85) * 5) * 0.55 - 0.6;
  if (b === BIOME.sky) {
    // Cloud floor: soft puffs with lit tops.
    const puff = fbm(x / 20, y / 14, 3, 86);
    f = 1 + (0.6 - puff) * 5;
  }
  if (b === BIOME.cyber) {
    // Dark panels with a neon grid every 16 px and dim sub-grid lines.
    if (x % 16 === 0 || y % 16 === 0) return (x + y) % 32 < 16 ? hex("#ff5ad8") : hex("#3ef0e0");
    if (x % 8 === 0 || y % 8 === 0) f += 1.1;
    f -= 0.6;
  }
  if (b === BIOME.autumn) {
    // Stubble rows and fallen leaves.
    if ((y + Math.floor(vnoise(x / 30, 0, 87) * 6)) % 7 === 0) f += 0.9;
    if (hash2(x, y, 88) > 0.992) return [hex("#ff8a3c"), hex("#ffd060"), hex("#c8402c")][Math.floor(hash2(x, y, 89) * 3)]!;
  }
  if (b === BIOME.haunted && fbm(x / 26, y / 18, 2, 90) > 0.62) f -= 0.9; // pale ground fog
  if (b === BIOME.jungle && hash2(x, y, 91) > 0.994) return hex("#ff5a8a");
  if (b === BIOME.blossom && hash2(x, y, 92) > 0.985) return hash2(x, y, 93) > 0.5 ? hex("#ffb3cc") : hex("#ffe1ec");
  if (b === BIOME.ruins) {
    // Worn flagstones showing through the moss.
    const lx = x % 14, ly = y % 10;
    if (vnoise(x / 40, y / 40, 94) > 0.55 && (lx === 0 || ly === 0)) f += 1.4;
  }
  if (c === G.Lush) f += 1.2;
  if (c === G.Forest) f = 4.4 + (mid - 0.5) * 1.2;
  return dither(ramp, f, x, y);
}

function texel(c: number, x: number, y: number, deep: number, biome: Biome = BIOME.temperate): Col {
  const b = biome as Exclude<Biome, 0>;
  switch (c) {
    case G.Grass:
    case G.Lush:
    case G.Forest: {
      if (biome !== BIOME.temperate) return biomeGround(c, x, y, b);
      const big = fbm(x / 46, y / 46, 2, 1);
      const mid = vnoise(x / 7, y / 7, 2);
      let f = 2.3 + (big - 0.5) * 2.4 + (mid - 0.5) * 0.9;
      if (c === G.Lush) f += 1.1;
      if (c === G.Forest) f = 4.6 + (mid - 0.5) * 1.2;
      const h = hash2(x, y, 3);
      if (h < 0.07) f -= 1.4; // lit blade tip
      else if (hash2(x, y - 1, 3) < 0.07) f += 1.2; // its shadowed stem
      else if (h > 0.985) f -= 2.2;
      if (c === G.Forest && hash2(x >> 1, y >> 1, 9) < 0.05) return W.bark[2]!;
      return dither(W.grass, f, x, y);
    }
    case G.Plaza: {
      if (biome === BIOME.cyber) {
        // Brushed metal deck plates with rivets and a lit seam.
        const lx = x % 12, ly = y % 12;
        if (lx === 11 || ly === 11) return hash2(x >> 2, y >> 2, 20) < 0.15 ? hex("#3ef0e0") : hex("#1a1830");
        if ((lx === 1 || lx === 9) && (ly === 1 || ly === 9)) return hex("#9aa4c0");
        const f = 1.2 + (hash2(Math.floor(x / 12), Math.floor(y / 12), 21) - 0.5) * 1.2 + (ly < 2 ? -0.6 : 0) + ((x + y) % 3 === 0 ? 0.3 : 0);
        return dither([hex("#6a6e92"), hex("#565a7c"), hex("#464a68"), hex("#383a56")], f, x, y);
      }
      const sx = Math.floor(x / 8), sy = Math.floor(y / 8);
      const lx = x & 7, ly = y & 7;
      if (lx === 7 || ly === 7) return W.plaza[3]!;
      const v = hash2(sx, sy, 12);
      let f = v < 0.3 ? 0.6 : v < 0.8 ? 1.2 : 1.8;
      if (lx === 0 || ly === 0) f -= 0.7;
      if (lx === 6 || ly === 6) f += 0.9;
      f += (vnoise(x / 20, y / 20, 13) - 0.5) * 0.8;
      if (hash2(x, y, 14) < 0.03) f += 1;
      return dither(W.plaza, f, x, y);
    }
    case G.Road: {
      if (biome === BIOME.cyber) {
        // Asphalt with a dashed neon centre line.
        const f = 1.6 + (vnoise(x / 9, y / 9, 22) - 0.5) * 1.2 + (hash2(x, y, 23) < 0.04 ? 1 : 0);
        if ((x % 32 === 15 || x % 32 === 16) && (y >> 3) % 2 === 0) return hex("#ffd060");
        return dither([hex("#4a4a64"), hex("#3a3a52"), hex("#2e2e44"), hex("#222236")], f, x, y);
      }
      // Warm brick lane in running bond, lightly worn in the middle.
      const bw = 8, bh = 4;
      const row = Math.floor(y / bh);
      const off = (row % 2) * (bw >> 1);
      const col = Math.floor((x + off) / bw);
      const lx = (x + off) % bw, ly = y % bh;
      if (lx === bw - 1 || ly === bh - 1) return hash2(x, y, 24) < 0.12 ? W.grass[3]! : BRICK_MORTAR;
      const v = hash2(col, row, 25);
      let f = 1.3 + (v - 0.5) * 1.6 + (vnoise(x / 26, y / 26, 26) - 0.5) * 1.2;
      if (ly === 0) f -= 0.8;
      if (lx === 0) f -= 0.4;
      if (ly === bh - 2) f += 0.6;
      return dither(BRICK, f, x, y);
    }
    case G.Dirt: {
      if (biome === BIOME.reef) {
        const plank = Math.floor(y / 4);
        if (y % 4 === 3) return PATH[b][4]!;
        const f = 1 + (hash2(plank, Math.floor((x + plank * 7) / 20), 35) - 0.5) * 1.6 + (x % 20 === 0 ? 2 : 0);
        return dither(PATH[b], f, x, y);
      }
      if (biome !== BIOME.temperate) {
        const f = 1.9 + (fbm(x / 22, y / 22, 2, 31) - 0.5) * 1.6 + (vnoise(x / 3, y / 3, 34) - 0.5) * 0.5 + (hash2(x, y, 32) < 0.03 ? 1 : 0);
        return dither(PATH[b], f, x, y);
      }
      let f = 1.9 + (fbm(x / 22, y / 22, 2, 31) - 0.5) * 1.6 + (vnoise(x / 3, y / 3, 34) - 0.5) * 0.5;
      if (hash2(x, y, 32) < 0.025) f += 1.2;
      else if (hash2(x, y - 1, 32) < 0.025) f -= 0.8;
      return dither(W.dirt, f, x, y);
    }
    case G.Rock: {
      // Boulder field: jittered cells, each a lit dome with a dark crevice between.
      const cell = 11;
      const cx = Math.floor(x / cell), cy = Math.floor(y / cell);
      let d1 = 99, d2 = 99, bx = 0, by = 0, bid = 0;
      for (let j = -1; j <= 1; j++) {
        for (let i = -1; i <= 1; i++) {
          const px = (cx + i + 0.15 + hash2(cx + i, cy + j, 44) * 0.7) * cell;
          const py = (cy + j + 0.15 + hash2(cx + i, cy + j, 45) * 0.7) * cell;
          const dd = Math.hypot(x + 0.5 - px, (y + 0.5 - py) * 1.2);
          if (dd < d1) { d2 = d1; d1 = dd; bx = px; by = py; bid = hash2(cx + i, cy + j, 46); }
          else if (dd < d2) d2 = dd;
        }
      }
      const rock = biome !== BIOME.temperate ? ROCK[b] : W.rock;
      if (d2 - d1 < 1.4) return hash2(x, y, 47) < 0.35 ? (biome ? GROUND[b][4]! : W.grass[4]!) : rock[5]!;
      const lam = -((x - bx) * 0.65 + (y - by) * 0.8) / cell;
      let f = 1.5 - lam * 2.6 + (bid - 0.5) * 0.9;
      if (d2 - d1 < 2.6) f += 1.1;
      if (hash2(x, y, 43) < 0.04) return biome ? rock[1]! : W.grass[2]!;
      return dither(rock, f, x, y);
    }
    case G.Sand: {
      const f = 1 + (vnoise(x / 6, y / 6, 51) - 0.5) * 1.4 + (hash2(x, y, 52) < 0.05 ? 1 : 0);
      return dither(biome ? SHORE[b] : W.sand, f, x, y);
    }
    case G.Water:
    default: {
      const f = 2 + Math.max(0, Math.min(1, (deep - 0.5) * 2)) * 3;
      return dither(W.water, f, x, y);
    }
  }
}

/** Precomputed caustic light web frames (64×64, tileable-ish). */
const CAUSTIC_N = 8;
let caustics: Uint8Array[] | null = null;
export function causticFrame(i: number): Uint8Array {
  if (!caustics) {
    caustics = [];
    for (let f = 0; f < CAUSTIC_N; f++) {
      const a = new Uint8Array(64 * 64);
      const ph = (f / CAUSTIC_N) * Math.PI * 2;
      for (let y = 0; y < 64; y++) {
        for (let x = 0; x < 64; x++) {
          const u = x + Math.sin(y * 0.2 + ph) * 2.2;
          const v = y + Math.cos(x * 0.2 + ph) * 2.2;
          const w1 = worley(((u % 64) + 64) % 64, ((v % 64) + 64) % 64, 16, 61);
          a[y * 64 + x] = Math.max(0, Math.min(255, Math.round((w1 - 0.55) * 900)));
        }
      }
      caustics.push(a);
    }
  }
  return caustics[((i % CAUSTIC_N) + CAUSTIC_N) % CAUSTIC_N]!;
}

/** Sprite for an object at time `t` (ms). */
export function objSprite(o: WorldObj, t: number, night: boolean, reduced: boolean, rustle = false): PropSprite {
  const frame = reduced ? 0 : Math.floor(t / 260 + o.tx * 0.6 + o.ty * 0.25) & 3;
  const b = (o.biome ?? BIOME.temperate) as Biome;
  switch (o.kind) {
    case "tree":
      if (b === BIOME.autumn) return treeSprite(o.variant % 100, "autumn");
      if (b === BIOME.blossom) return treeSprite(o.variant % 100, "blossom");
      return b ? tintFoliage(treeSprite(o.variant % 100, "round"), b) : treeSprite(o.variant % 100, o.variant >= 100 ? "blossom" : "round");
    case "pine": return b === BIOME.snow ? snowCapped(pineSprite(o.variant)) : pineSprite(o.variant);
    case "boulder":
      if (b === BIOME.haunted) return tombSprite(o.variant);
      if (b === BIOME.ruins) return ruinSprite(o.variant * 3 + 1);
      return tintFoliage(tintStone(boulderSprite(o.variant), b), b);
    case "cactus": return cactusSprite(o.variant);
    case "deadtree": return deadTreeSprite(o.variant, b === BIOME.volcano ? "charred" : b === BIOME.haunted ? "haunted" : "mossy");
    case "jungletree": return jungleTreeSprite(o.variant);
    case "hay": return hayBaleSprite(o.variant);
    case "tomb": return tombSprite(o.variant);
    case "stonelantern": return stoneLanternSprite();
    case "ruin": return ruinSprite(o.variant);
    case "spire": return spireSprite(o.variant);
    case "stalagmite": return stalagmiteSprite(o.variant);
    case "shroom": return shroomSprite(o.variant);
    case "palm": return palmSprite(o.variant);
    case "coral": return coralSprite(o.variant);
    case "cloudbush": return cloudBushSprite(o.variant);
    case "pillar": return pillarSprite(o.variant);
    case "holotree": return holoTreeSprite(o.variant);
    case "pylon": return pylonSprite(o.variant);
    case "lamp": return lampSprite();
    case "crate": return crateSprite(o.variant);
    case "barrel": return barrelSprite();
    case "fence": return fenceSprite(o.fence?.[0] ?? false, o.fence?.[1] ?? false);
    case "sign": return signSprite();
    case "reed": return tintFoliage(reedSprite(o.variant, frame), b);
    case "kettle": return kettleSprite();
    case "tall": return tintFoliage(tallTuft(o.variant, frame, rustle), b);
    case "building": {
      const b = o.building!;
      return buildingSprite(b.kind, b.tw, b.th, b.doors, night);
    }
  }
}
